import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal, staggerContainer, staggerItem } from '../hooks/useScrollReveal';

const Pricing = () => {
  const { ref, isInView } = useScrollReveal();
  const { t } = useLanguage();

  return (
    <section id="pricing" className="section-padding bg-gradient-to-b from-black to-gray-900">
      <div className="container-max">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="reveal-section"
        >
          <motion.h2
            variants={staggerItem}
            className="text-4xl md:text-5xl font-bold text-center mb-16"
          >
            <span className="text-primary-400">{t.pricing.title}</span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Individual Training */}
            <motion.div
              variants={staggerItem}
              className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg overflow-hidden shadow-lg border border-primary-500/20 hover:border-primary-400/40 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="bg-gray-800 px-6 py-4 relative border-b border-primary-500/20">
                <h3 className="text-white text-xl font-semibold mb-1">
                  {t.pricing.individual.title}
                </h3>
              </div>

              <div className="px-6 py-6">
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {t.pricing.individual.description}
                </p>

                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3 text-sm">{t.pricing.individual.pricingByPeopleTitle}</h4>
                  <div className="space-y-2">
                    {t.pricing.individual.pricingOptions.map((option, idx) => (
                      <div key={idx} className="flex justify-between items-center text-gray-300">
                        <span className="text-sm">{option.label}:</span>
                        <span className="text-primary-400 font-semibold">
                          {option.price} {option.period && <span className="text-primary-300 text-xs">{option.period}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3 text-sm">{t.pricing.individual.pricingByFrequencyTitle}</h4>
                  <div className="space-y-2">
                    {t.pricing.individual.frequencyOptions.map((option, idx) => (
                      <div key={idx} className="flex justify-between items-center text-gray-300">
                        <span className="text-sm">{option.label}:</span>
                        <span className="text-primary-400 font-semibold">
                          {option.price} {option.period && <span className="text-primary-300 text-xs">{option.period}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {t.pricing.individual.features.map((feature, idx) => (
                    <li key={idx} className="text-gray-300 flex items-start">
                      <Check className="w-5 h-5 text-primary-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {t.pricing.individual.disclaimer && (
                  <p className="text-gray-400 text-xs italic mt-4">
                    {t.pricing.individual.disclaimer}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Online Individual Programming */}
            <motion.div
              variants={staggerItem}
              className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg overflow-hidden shadow-lg border border-primary-500/20 hover:border-primary-400/40 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="bg-gray-800 px-6 py-4 relative border-b border-primary-500/20">
                <h3 className="text-white text-xl font-semibold mb-1">
                  {t.pricing.online.title}
                </h3>
                <div className="text-primary-400 text-sm mt-1">
                  {t.pricing.online.period}
                </div>
              </div>

              <div className="px-6 py-6">
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {t.pricing.online.description}
                </p>

                {/* Athlete Plan */}
                <div className="mb-6 pb-6 border-b border-primary-500/20">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-white font-semibold">{t.pricing.online.athlete.title}</h4>
                    <div className="text-primary-400 text-xl font-bold">
                      {t.pricing.online.athlete.price}
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {t.pricing.online.athlete.features.map((feature, idx) => (
                      <li key={idx} className="text-gray-300 flex items-start">
                        <Check className="w-4 h-4 text-primary-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Basic Plan */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-white font-semibold">{t.pricing.online.basic.title}</h4>
                    <div className="text-primary-400 text-xl font-bold">
                      {t.pricing.online.basic.price}
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {t.pricing.online.basic.features.map((feature, idx) => (
                      <li key={idx} className="text-gray-300 flex items-start">
                        <Check className="w-4 h-4 text-primary-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
